import type { CameraState } from '../../types';
import { clampPitch, getCameraBasis } from '../../geometry/projection';
import { toViewProjectionMatrix } from '../../geometry/viewProjection';
import { toWorldLightDirection, type LightingModel } from '../lighting';
import {
  GRID_INK_LIGHTNESS_SPLIT,
  type DrapedGrid,
  type DrapedGridAxis,
} from '../drapedGrid';

/**
 * How many lights the shader's uniform struct holds.
 *
 * Fixed rather than variable because a uniform buffer is a fixed-size struct; a storage buffer
 * would allow any number and costs a bind-group entry and a loop bound for no benefit today, since
 * the rig is two lights and there is no control that adds a third.
 */
export const SHADER_LIGHT_COUNT = 2;

/**
 * Every field is `vec4`-sized, in a deliberately over-padded layout.
 *
 * WGSL aligns a `vec3` to 16 bytes but sizes it as 12, so a struct of `vec3`s develops invisible
 * padding that a hand-packed buffer gets wrong - and the failure mode is not an error but a shader
 * reading a field from the wrong offset, which on a GPU that cannot be stepped through is close to
 * undebuggable. Packing everything as `vec4` makes offsets trivially predictable and costs a
 * handful of wasted floats in a buffer that is written once per frame.
 *
 * Offsets in floats, matching the `Uniforms` struct in the shader exactly. The two must be changed
 * together, which is what the layout test guards.
 */
export const UNIFORM_OFFSETS = {
  viewProjection: 0,
  basisRight: 16,
  basisUp: 20,
  basisForward: 24,
  /** Per light: xyz world-space direction, w diffuse. */
  lightDirection: 28,
  /** Per light: rgb colour, w specular. */
  lightColor: 36,
  /** rgb fog colour, w strength. */
  fog: 44,
  /** ambient, shininess, shading, depthExtent. */
  params: 48,
  /** zMin, zSpan, heightScale, cel steps (0 is smooth shading). */
  field: 52,
  /** Draped grid lines along x: first, spacing, count, minor divisions. */
  gridX: 56,
  /** Draped grid lines along y: first, spacing, count, minor divisions. */
  gridY: 60,
  /** Draped grid ink over light surface: rgb 0-1, w opacity (0 draws no grid). */
  gridInk: 64,
  /** x 1 when the surface is colored from the paint texture rather than the ramp; yzw unused. */
  paint: 68,
  /** Draped grid ink over dark surface: rgb 0-1, w the lightness above which a surface is light. */
  gridInkOnDark: 72,
} as const;

/** Total size in floats. 16-byte aligned, as a uniform buffer binding must be. */
export const UNIFORM_FLOAT_COUNT = 76;

export type SurfaceUniformArgs = {
  camera: CameraState;
  viewport: { width: number; height: number };
  lighting: LightingModel;
  /** Mixes between the flat ramp colour (0) and fully lit (1). */
  shading: number;
  /** The field's own value range, so the shader can normalise a value for the ramp lookup. */
  zRange: readonly [number, number];
  drapedGrid: DrapedGrid | null;
  /** Whether a paint texture colors the surface in place of the ramp. */
  painted: boolean;
  celSteps: number;
};

const packGridAxis = (
  data: Float32Array,
  at: number,
  { first, spacing, count, divisions }: DrapedGridAxis
): void => {
  data[at] = first;
  data[at + 1] = spacing;
  data[at + 2] = count;
  data[at + 3] = divisions;
};

/**
 * Packs the frame's uniforms into the layout the shader declares.
 *
 * Light directions are resolved to world space here rather than in the shader: the view-space
 * resolution needs the camera basis and is already tested on the CPU, so doing it once per frame
 * instead of once per fragment is both cheaper and less code to get wrong somewhere unobservable.
 */
export const packSurfaceUniforms = ({
  camera,
  viewport,
  lighting,
  shading,
  zRange,
  drapedGrid,
  painted,
  celSteps,
}: SurfaceUniformArgs): Float32Array => {
  const data = new Float32Array(UNIFORM_FLOAT_COUNT);
  const basis = getCameraBasis(camera.yaw, clampPitch(camera.pitch));

  data.set(
    toViewProjectionMatrix(camera, viewport),
    UNIFORM_OFFSETS.viewProjection
  );
  data.set(basis.right, UNIFORM_OFFSETS.basisRight);
  data.set(basis.up, UNIFORM_OFFSETS.basisUp);
  data.set(basis.forward, UNIFORM_OFFSETS.basisForward);

  for (let index = 0; index < SHADER_LIGHT_COUNT; index++) {
    /* A rig with fewer lights than the struct holds pads with zero-diffuse entries rather than
       shortening the loop, so the shader needs no count and cannot read uninitialised memory. */
    const light = lighting.lights[index];
    const directionAt = UNIFORM_OFFSETS.lightDirection + index * 4;
    const colorAt = UNIFORM_OFFSETS.lightColor + index * 4;

    if (!light) {
      data[directionAt + 2] = 1;
      continue;
    }

    data.set(toWorldLightDirection(light, basis), directionAt);
    data[directionAt + 3] = light.diffuse;
    data.set(light.color, colorAt);
    data[colorAt + 3] = light.specular;
  }

  data.set(lighting.fog.color, UNIFORM_OFFSETS.fog);
  data[UNIFORM_OFFSETS.fog + 3] = lighting.fog.strength;

  data[UNIFORM_OFFSETS.params] = lighting.ambient;
  data[UNIFORM_OFFSETS.params + 1] = lighting.shininess;
  data[UNIFORM_OFFSETS.params + 2] = shading;
  /* Half-extent of the cube the camera is handed, which bounds |depth|. The shader needs it twice:
     to turn depth into a 0-to-1 nearness for the fog, and to write clip-space z for the depth
     test - neither of which the view-projection matrix carries, since the software path computes
     depth separately. */
  data[UNIFORM_OFFSETS.params + 3] = Math.hypot(1, 1, camera.heightScale);

  const [zMin, zMax] = zRange;
  data[UNIFORM_OFFSETS.field] = zMin;
  data[UNIFORM_OFFSETS.field + 1] = zMax - zMin || 1;
  data[UNIFORM_OFFSETS.field + 2] = camera.heightScale;
  data[UNIFORM_OFFSETS.field + 3] = celSteps;

  if (drapedGrid) {
    packGridAxis(data, UNIFORM_OFFSETS.gridX, drapedGrid.x);
    packGridAxis(data, UNIFORM_OFFSETS.gridY, drapedGrid.y);
    data.set(
      drapedGrid.inkOnLight.map(channel => channel / 255),
      UNIFORM_OFFSETS.gridInk
    );
    data[UNIFORM_OFFSETS.gridInk + 3] = drapedGrid.opacity;
    data.set(
      drapedGrid.inkOnDark.map(channel => channel / 255),
      UNIFORM_OFFSETS.gridInkOnDark
    );
    data[UNIFORM_OFFSETS.gridInkOnDark + 3] = GRID_INK_LIGHTNESS_SPLIT;
  }

  data[UNIFORM_OFFSETS.paint] = painted ? 1 : 0;

  return data;
};
